import {
  DocumentSendDocumentType,
  DocumentSendFormat,
  type DocumentSendFilters,
  type DocumentSendProtheusParty,
  type DocumentSendSortField,
} from '@/entities/DocumentSend';
import { isSortOrder, type SearchFilter, type SortOrder } from '@/shared/lib';

const DOCUMENT_TYPE_VALUES = new Set<string>(Object.values(DocumentSendDocumentType));
const FORMAT_VALUES = new Set<string>(Object.values(DocumentSendFormat));
const SORT_FIELD_VALUES = new Set<DocumentSendSortField>(['title', 'sendDate']);

export interface DocumentSendControlQueryState {
  currentPage: number;
  filters: DocumentSendFilters;
  order: SortOrder | '';
  searchTerm: string;
  sort: DocumentSendSortField | '';
}

function readQueryString(value: unknown): string {
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : '';
  }

  return typeof value === 'string' ? value : '';
}

function readQueryPage(value: unknown): number {
  const parsedPage = Number(readQueryString(value));

  if (!Number.isInteger(parsedPage) || parsedPage < 1) {
    return 1;
  }

  return parsedPage;
}

function readQueryStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === 'string' && entry !== '');
  }

  if (typeof value === 'string' && value.length > 0) {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
  }

  return [];
}

function readDocumentTypesQuery(value: unknown): DocumentSendDocumentType[] {
  return readQueryStringArray(value).filter((entry): entry is DocumentSendDocumentType =>
    DOCUMENT_TYPE_VALUES.has(entry)
  );
}

function readFormatsQuery(value: unknown): DocumentSendFormat[] {
  return readQueryStringArray(value).filter((entry): entry is DocumentSendFormat =>
    FORMAT_VALUES.has(entry)
  );
}

function readProtheusPartyQuery(
  code: unknown,
  store: unknown
): DocumentSendProtheusParty | undefined {
  const rawCode = readQueryString(code);
  const rawStore = readQueryString(store);

  return rawCode !== '' && rawStore !== '' ? { code: rawCode, store: rawStore } : undefined;
}

function areProtheusPartiesEqual(
  left?: DocumentSendProtheusParty,
  right?: DocumentSendProtheusParty
): boolean {
  return left?.code === right?.code && left?.store === right?.store;
}

function readBooleanQuery(value: unknown): boolean | undefined {
  const raw = readQueryString(value);

  if (raw === '') {
    return undefined;
  }

  return raw === 'true' ? true : undefined;
}

function readSendDateQuery(value: unknown): string | undefined {
  const raw = readQueryString(value);
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : undefined;
}

function readSortQuery(value: unknown): DocumentSendSortField | '' {
  const raw = readQueryString(value);
  return SORT_FIELD_VALUES.has(raw as DocumentSendSortField) ? (raw as DocumentSendSortField) : '';
}

function readOrderQuery(value: unknown): SortOrder | '' {
  const raw = readQueryString(value);
  return isSortOrder(raw) ? raw : '';
}

function readFiltersFromQuery(query: Record<string, unknown>): DocumentSendFilters {
  const filters: DocumentSendFilters = {};

  const documentTypes = readDocumentTypesQuery(query.documentTypes);
  if (documentTypes.length > 0) {
    filters.documentTypes = documentTypes;
  }

  const formats = readFormatsQuery(query.formats);
  if (formats.length > 0) {
    filters.formats = formats;
  }

  const hasElectronicCopy = readBooleanQuery(query.hasElectronicCopy);
  if (hasElectronicCopy === true) {
    filters.hasElectronicCopy = true;
  }

  const protheusClient = readProtheusPartyQuery(
    query.protheusClientCode,
    query.protheusClientStore
  );
  if (protheusClient !== undefined) {
    filters.protheusClient = protheusClient;
  }

  const protheusSupplier = readProtheusPartyQuery(
    query.protheusSupplierCode,
    query.protheusSupplierStore
  );
  if (protheusSupplier !== undefined) {
    filters.protheusSupplier = protheusSupplier;
  }

  const sendDate = readSendDateQuery(query.sendDate);
  if (sendDate !== undefined) {
    filters.sendDate = sendDate;
  }

  return filters;
}

export function readDocumentSendControlQuery(
  query: Record<string, unknown>
): DocumentSendControlQueryState {
  return {
    currentPage: readQueryPage(query.page),
    filters: readFiltersFromQuery(query),
    order: readOrderQuery(query.order),
    searchTerm: readQueryString(query.term),
    sort: readSortQuery(query.sort),
  };
}

export function hasActiveDocumentSendFilters(filters: DocumentSendFilters): boolean {
  return (
    (filters.documentTypes?.length ?? 0) > 0 ||
    (filters.formats?.length ?? 0) > 0 ||
    filters.hasElectronicCopy === true ||
    filters.protheusClient !== undefined ||
    filters.protheusSupplier !== undefined ||
    Boolean(filters.sendDate)
  );
}

export function areDocumentSendFiltersEqual(
  leftFilters: DocumentSendFilters,
  rightFilters: DocumentSendFilters
): boolean {
  const arraysEqual = (left?: string[], right?: string[]): boolean => {
    if (!left && !right) return true;
    if (!left || !right) return false;
    if (left.length !== right.length) return false;

    const sortedLeft = [...left].sort();
    const sortedRight = [...right].sort();

    return sortedLeft.every((value, index) => value === sortedRight[index]);
  };

  return (
    arraysEqual(leftFilters.documentTypes, rightFilters.documentTypes) &&
    arraysEqual(leftFilters.formats, rightFilters.formats) &&
    Boolean(leftFilters.hasElectronicCopy) === Boolean(rightFilters.hasElectronicCopy) &&
    areProtheusPartiesEqual(leftFilters.protheusClient, rightFilters.protheusClient) &&
    areProtheusPartiesEqual(leftFilters.protheusSupplier, rightFilters.protheusSupplier) &&
    (leftFilters.sendDate ?? '') === (rightFilters.sendDate ?? '')
  );
}

export function normalizeDocumentSendSortOptions(
  nextSort: string,
  nextOrder: string
): { order: SortOrder | ''; sort: DocumentSendSortField | '' } {
  return {
    sort: SORT_FIELD_VALUES.has(nextSort as DocumentSendSortField)
      ? (nextSort as DocumentSendSortField)
      : '',
    order: isSortOrder(nextOrder) ? nextOrder : '',
  };
}

export function buildDocumentSendControlFilterQuery(filters: DocumentSendFilters): SearchFilter {
  const filterQuery: SearchFilter = {};

  if (filters.documentTypes && filters.documentTypes.length > 0) {
    filterQuery.documentTypes = filters.documentTypes.join(',');
  }
  if (filters.formats && filters.formats.length > 0) {
    filterQuery.formats = filters.formats.join(',');
  }
  if (filters.hasElectronicCopy === true) {
    filterQuery.hasElectronicCopy = 'true';
  }
  if (filters.protheusClient) {
    filterQuery.protheusClientCode = filters.protheusClient.code;
    filterQuery.protheusClientStore = filters.protheusClient.store;
  }
  if (filters.protheusSupplier) {
    filterQuery.protheusSupplierCode = filters.protheusSupplier.code;
    filterQuery.protheusSupplierStore = filters.protheusSupplier.store;
  }
  if (filters.sendDate) {
    filterQuery.sendDate = filters.sendDate;
  }

  return filterQuery;
}
